import { useEffect, useRef, ReactNode } from "react";
import {
  useTable,
  useSortBy,
  usePagination,
  useFilters,
  Column,
  SortingRule,
} from "react-table";
import cs from "classnames";
import { Text } from "../Type";
import {
  SortSmall,
  ArrowBottom,
  ArrowTop,
  ChevronLeft,
  ChevronRight,
} from "../Icons/general-interface-1";
import styles from "./index.module.scss";

export type PropsType = {
  columns: Column<{ [key: string]: unknown }>[];
  data: { [key: string]: unknown }[];
  filters?: { id: string; value: unknown }[];
  fetchData: ({
    pageIndex,
    pageSize,
    sortBy,
  }: {
    pageIndex: number;
    pageSize: number;
    sortBy: SortingRule<{ [key: string]: unknown }>[];
    filters?: unknown;
    search?: string;
  }) => void;
  pageCount: number;
  total: number;
  loading?: boolean;
  emptyView?: ReactNode | ReactNode[];
  initialState?: { sortBy: { id: string; desc: boolean }[] };
  handleRowClick?: (row: { original }) => void;
  resetKey?: string;
};

// function DefaultColumnFilter({ column: { filterValue, setFilter } }) {
//   return (
//     <input
//       value={filterValue || ""}
//       onChange={(e) => {
//         setFilter(e.target.value || undefined); // Set undefined to remove the filter entirely
//       }}
//     />
//   );
// }

const Table = ({
  columns,
  data,
  fetchData,
  pageCount: controlledPageCount,
  total,
  loading,
  emptyView,
  initialState,
  filters,
  handleRowClick,
  resetKey,
}: //setFilters,
PropsType) => {
  // const defaultColumn = useMemo(
  //   () => ({
  //     // Let's set up our default Filter UI
  //     Filter: DefaultColumnFilter,
  //   }),
  //   []
  // );

  const tableInstance = useTable(
    {
      columns,
      data,
      disableMultiSort: true,
      manualSortBy: true,
      manualPagination: true,
      manualFilters: true,
      pageCount: controlledPageCount,
      initialState,
    },
    useFilters,
    useSortBy,
    usePagination
  );
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    setPageSize,
    setAllFilters,
    gotoPage,
    state: { pageIndex, pageSize, sortBy },
  } = tableInstance;

  useEffect(() => {
    fetchData({ pageIndex, pageSize, sortBy });
  }, [fetchData, pageIndex, pageSize, sortBy]);

  useEffect(() => {
    if (filters && filters.length) {
      setAllFilters(filters);
    } else {
      setAllFilters([]);
    }
  }, [filters, setAllFilters]);

  // Reset pagination to page 0 when filters or search change.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (didMountRef.current) {
      gotoPage(0);
    } else {
      didMountRef.current = true;
    }
  }, [resetKey, gotoPage]);
  const hasData = (() => rows && rows.length > 0)();

  return (
    <>
      <table
        cellPadding={0}
        cellSpacing={0}
        {...getTableProps()}
        className={cs(styles.table, loading && styles.loading)}
      >
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps()}
                  className={cs(styles.th, !hasData && styles.noData)}
                >
                  <div className={styles.header}>
                    {column.canSort ? (
                      <button
                        type="button"
                        {...column.getSortByToggleProps()}
                        className={styles.sortButton}
                        disabled={!hasData}
                      >
                        {column.render("Header")}
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <ArrowBottom className={styles.arrow} />
                          ) : (
                            <ArrowTop className={styles.arrow} />
                          )
                        ) : (
                          <SortSmall className={styles.sort} />
                        )}
                      </button>
                    ) : (
                      column.render("Header")
                    )}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        {hasData && (
          <tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row);
              return (
                <tr
                  {...row.getRowProps()}
                  className={cs(styles.tr, {
                    [styles.trCanClick]: !!handleRowClick,
                  })}
                  onClick={handleRowClick && (() => handleRowClick(row))}
                >
                  {row.cells.map((cell) => {
                    return (
                      <td {...cell.getCellProps()} className={styles.td}>
                        {cell.render("Cell")}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        )}
      </table>
      {emptyView && !hasData && !loading ? (
        emptyView
      ) : (
        <div className={styles.pagination}>
          <div className={styles.paginationItem}>
            <Text size="small" className={styles.textFade}>
              Row per page:
            </Text>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
              }}
              className={styles.select}
            >
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  {pageSize}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.paginationItem}>
            <Text size="small" className={styles.textFade}>
              {`${pageIndex * pageSize + 1} - ${
                (pageIndex + 1) * pageSize < total
                  ? Math.max((pageIndex + 1) * pageSize, controlledPageCount)
                  : total
              } of ${total} items`}
            </Text>
            <button
              type="button"
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
              className={styles.button}
            >
              <ChevronLeft />
            </button>
            <button
              type="button"
              onClick={() => nextPage()}
              disabled={!canNextPage}
              className={styles.button}
            >
              <ChevronRight />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Table;
